import type { CSSProperties, ReactNode } from 'react';
import { Link as RouterLink, useInRouterContext, useLocation, type LinkProps } from 'react-router-dom';
import { Typography } from 'antd';
import { adminLinks } from './adminLinks';

type Person = { kind: 'listener'; id: string; tab?: string } | { kind: 'user'; id: string };

/**
 * Where a person's name links to: their details OPENED BESIDE THE CURRENT
 * PAGE (layout/PersonPane), keeping every filter the page has in its URL.
 * On the Listeners / Users pages the page's own pane params are used; on the
 * other pages the tab goes in `dtab` so a page's own `?tab=` is untouched.
 * One person at a time: setting one kind clears the other.
 */
function personSearch(search: string, pathname: string, p: Person): string {
  const next = new URLSearchParams(search);
  next.delete('listener');
  next.delete('user');
  next.delete('dtab');
  // On /listeners the pane's tab is the page's `tab`; elsewhere `tab` belongs
  // to the page (e.g. Payouts) and the pane's tab is `dtab`.
  const onListeners = pathname === '/listeners';
  if (onListeners) next.delete('tab');
  if (p.kind === 'listener') {
    next.set('listener', p.id);
    if (p.tab) next.set(onListeners ? 'tab' : 'dtab', p.tab);
  } else {
    next.set('user', p.id);
  }
  const q = next.toString();
  return q ? `?${q}` : '';
}

/** In-place person link (router only — see [personSearch]). */
function PersonLink({ person, children, ...rest }: Omit<LinkProps, 'to'> & { person: Person }) {
  const { pathname, search } = useLocation();
  return (
    <RouterLink to={{ pathname, search: personSearch(search, pathname, person) }} {...rest}>
      {children}
    </RouterLink>
  );
}

/** The person link that fits where it is rendered: in place inside the app, a plain address outside a router. */
function PersonAnchor({ person, children, ...rest }: Omit<LinkProps, 'to'> & { person: Person }) {
  const inRouter = useInRouterContext();
  if (inRouter) {
    return (
      <PersonLink person={person} {...rest}>
        {children}
      </PersonLink>
    );
  }
  const href = person.kind === 'listener' ? adminLinks.listener(person.id, person.tab) : adminLinks.user(person.id);
  return (
    <a href={href} {...rest}>
      {children}
    </a>
  );
}

/**
 * THE admin-facing name of a listener — one rule for the whole panel.
 *
 *   valid Display Name  →  the Display Name
 *   otherwise           →  the existing fallback (@handle, then a short id)
 *
 * A female listener is created with a registration @handle and sets her
 * Display Name afterwards; that Display Name is what callers see, so it is what
 * the admin must see too — never the internal handle on one page and the name
 * on another. The same rule covers every other listener, so no page needs to
 * know the listener's gender to pick a name.
 *
 * Presentation only: ids, auth and business logic never use this value.
 */
export interface ListenerNameSource {
  displayName?: string | null;
  /** The account's @handle (without the @). */
  handle?: string | null;
  /** Listener id — last resort when there is neither a name nor a handle. */
  id?: string | null;
}

/** A Display Name counts only if it has something in it besides whitespace. */
export function validDisplayName(name: string | null | undefined): string | null {
  const trimmed = name?.trim();
  return trimmed ? trimmed : null;
}

/**
 * "@handle" for display — whether or not the stored value already starts with
 * "@" (never "@@x"); null for an empty / missing handle. Presentation only: the
 * stored handle is never rewritten.
 */
export function formatHandle(handle: string | null | undefined): string | null {
  const bare = handle?.trim().replace(/^@+/, '').trim();
  return bare ? `@${bare}` : null;
}

/** The name to show for a listener. */
export function listenerLabel(l: ListenerNameSource | null | undefined): string {
  if (!l) return '—';
  const name = validDisplayName(l.displayName);
  if (name) return name;
  const handle = formatHandle(l.handle);
  if (handle) return handle;
  return l.id ? `Listener ${l.id.slice(0, 8)}` : '—';
}

/**
 * The secondary identifier under the name, so two listeners with the same
 * Display Name can still be told apart: the @handle when the label is a
 * Display Name, otherwise the short id.
 */
export function listenerSecondary(l: ListenerNameSource | null | undefined): string | null {
  if (!l) return null;
  const handle = formatHandle(l.handle);
  if (validDisplayName(l.displayName)) return handle ?? (l.id ? l.id.slice(0, 8) : null);
  if (handle) return l.id ? l.id.slice(0, 8) : null;
  return null;
}

/** "Meera (@user_8841)" — for plain-text places: exports, titles, search rows. */
export function listenerText(l: ListenerNameSource | null | undefined): string {
  const secondary = listenerSecondary(l);
  return secondary ? `${listenerLabel(l)} (${secondary})` : listenerLabel(l);
}

/**
 * A listener, the way every table, drawer and widget shows one: the Display
 * Name (linked to Listener details when the id is known), with the identifier
 * underneath or inline.
 */
export function ListenerName({
  listener,
  listenerId,
  tab,
  onOpen,
  secondary = 'below',
  strong = true,
  style,
}: {
  listener: ListenerNameSource | null | undefined;
  /** Links to this listener's details when set. */
  listenerId?: string | null;
  /** Listener details tab to open. */
  tab?: string;
  /**
   * Open the details in place (the Listeners page's own master–detail pane,
   * which keeps its filters) instead of navigating to `adminLinks.listener`.
   */
  onOpen?: (listenerId: string) => void;
  secondary?: 'below' | 'inline' | 'none';
  strong?: boolean;
  style?: CSSProperties;
}) {
  const label = listenerLabel(listener);
  const sub = secondary === 'none' ? null : listenerSecondary(listener);
  const nameStyle: CSSProperties = { fontWeight: strong ? 600 : undefined, ...style };
  const name: ReactNode = listenerId && onOpen ? (
    <a
      style={nameStyle}
      title="Open listener details"
      href={adminLinks.listener(listenerId, tab)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen(listenerId);
      }}
    >
      {label}
    </a>
  ) : listenerId ? (
    <PersonAnchor
      person={{ kind: 'listener', id: listenerId, tab }}
      style={nameStyle}
      onClick={(e) => e.stopPropagation()}
      title="Open listener details"
    >
      {label}
    </PersonAnchor>
  ) : (
    <span style={nameStyle}>{label}</span>
  );
  if (!sub) return name;
  const subText = (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {sub}
    </Typography.Text>
  );
  return secondary === 'inline' ? (
    <span>
      {name} {subText}
    </span>
  ) : (
    <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.3 }}>
      {name}
      {subText}
    </span>
  );
}

/**
 * THE admin-facing name of a user (caller) — the user-side twin of the listener
 * rule: their Display Name first, with the @handle underneath; just the
 * @handle when they never set a Display Name; then a short id; then a dash.
 */
export interface UserNameSource {
  /** The user's own Display Name (users.display_name) — NOT a listener name. */
  displayName?: string | null;
  handle?: string | null;
  /** User id — last resort when there is neither a name nor a handle. */
  id?: string | null;
}

export function userLabel(u: UserNameSource | null | undefined): string {
  if (!u) return '—';
  return (
    validDisplayName(u.displayName) ??
    formatHandle(u.handle) ??
    (u.id ? `User ${u.id.slice(0, 8)}` : '—')
  );
}

/** The @handle under a Display Name; null when the label already is the handle. */
export function userSecondary(u: UserNameSource | null | undefined): string | null {
  if (!u || !validDisplayName(u.displayName)) return null;
  return formatHandle(u.handle);
}

/** "John Doe (@johndoe)" — for plain-text places: modal titles, exports, search rows. */
export function userText(u: UserNameSource | null | undefined): string {
  const secondary = userSecondary(u);
  return secondary ? `${userLabel(u)} (${secondary})` : userLabel(u);
}

/**
 * A user (caller), linked to their details when the id is known: Display Name
 * with the @handle underneath (or inline), or just the @handle.
 */
export function UserName({
  handle,
  displayName,
  userId,
  strong = false,
  secondary = 'below',
}: {
  handle: string | null | undefined;
  /** The user's own Display Name, when the API sends it. */
  displayName?: string | null;
  userId?: string | null;
  strong?: boolean;
  secondary?: 'below' | 'inline' | 'none';
}) {
  const source = { displayName, handle, id: userId };
  const label = userLabel(source);
  const sub = secondary === 'none' ? null : userSecondary(source);
  const style: CSSProperties = { fontWeight: strong || sub ? 600 : undefined };
  const name: ReactNode = userId ? (
    <PersonAnchor person={{ kind: 'user', id: userId }} style={style} onClick={(e) => e.stopPropagation()} title="Open user details">
      {label}
    </PersonAnchor>
  ) : (
    <span style={style}>{label}</span>
  );
  if (!sub) return name;
  const subText = (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {sub}
    </Typography.Text>
  );
  return secondary === 'inline' ? (
    <span>
      {name} {subText}
    </span>
  ) : (
    <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.3 }}>
      {name}
      {subText}
    </span>
  );
}

/**
 * Someone who may or may not be a listener (a reported account, a party to a
 * block): shown by the listener rule when they have a Display Name, by @handle
 * otherwise, and linked to Listener details when the listener id is known, to
 * User details when only the user id is.
 */
export function PersonName({
  displayName,
  userDisplayName,
  handle,
  userId,
  listenerId,
}: {
  /** The LISTENER Display Name (listeners.display_name), when they are one. */
  displayName?: string | null;
  /** The account's own user Display Name (users.display_name). */
  userDisplayName?: string | null;
  handle?: string | null;
  userId?: string | null;
  listenerId?: string | null;
}) {
  if (listenerId) {
    return (
      <ListenerName
        listener={{ displayName: validDisplayName(displayName) ?? userDisplayName, handle, id: listenerId }}
        listenerId={listenerId}
      />
    );
  }
  if (!validDisplayName(displayName)) {
    return <UserName handle={handle} displayName={userDisplayName} userId={userId} strong />;
  }
  const label = listenerLabel({ displayName, handle });
  const sub = listenerSecondary({ displayName, handle, id: userId });
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.3 }}>
      {userId ? (
        <PersonAnchor person={{ kind: 'user', id: userId }} style={{ fontWeight: 600 }} onClick={(e) => e.stopPropagation()}>
          {label}
        </PersonAnchor>
      ) : (
        <span style={{ fontWeight: 600 }}>{label}</span>
      )}
      {sub && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {sub}
        </Typography.Text>
      )}
    </span>
  );
}
